import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Trash2, AlertTriangle, CheckCircle2, RefreshCw, FileWarning } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Widget Super Admin pour purger le cache PDF (R2 + disque) après une
 * amélioration du template (sommaire, mini-QR scan-to-jump…).
 *
 * Mode 1 : DRAFT uniquement (safe — souscriptions non finalisées seront régénérées
 *          à la prochaine consultation).
 * Mode 2 : DRAFT + finalized (action plus lourde, opt-in via case à cocher).
 */
export default function PdfCachePurgeWidget() {
  const [purging, setPurging] = useState(false);
  const [includeFinalized, setIncludeFinalized] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handlePurge = async () => {
    setPurging(true);
    try {
      const res = await axios.post(
        `${API}/admin/pdf-cache/purge`,
        {
          souscription_ids: null,            // null = toutes
          packs: ['pack2_conditions'],
          include_drafts: true,
          include_finalized: includeFinalized,
        },
        { headers: getAuthHeader() },
      );
      setLastResult(res.data);
      toast.success(
        `Cache purgé : ${res.data.r2_deleted} R2 + ${res.data.disk_deleted} disque`,
        { description: res.data.errors?.length ? `${res.data.errors.length} erreur(s)` : undefined },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la purge');
    } finally {
      setPurging(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Card className="overflow-hidden border-slate-200 hover:shadow-lg transition-all" data-testid="pdf-cache-purge-widget">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-3 bg-gradient-to-br from-amber-500 to-orange-600 text-white p-6 flex flex-col items-center justify-center text-center">
            <div className="flex items-center gap-2 mb-2 opacity-90">
              <FileWarning className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">Cache PDF</span>
            </div>
            <div className="w-24 h-24 rounded-full bg-white/15 ring-4 ring-amber-300 flex items-center justify-center backdrop-blur-sm">
              <Trash2 className="h-10 w-10" />
            </div>
            <div className="mt-3 text-sm font-bold">Purge R2 + disque</div>
            <div className="text-xs opacity-80">PDFs régénérables</div>
          </div>

          <div className="lg:col-span-9 p-6 space-y-4">
            <div>
              <h3 className="font-semibold text-slate-900">Cache PDF — Forcer la régénération</h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Supprime les Pack 2 (Conditions + Couverture) cachés sur R2 et sur le disque local.
                Au prochain téléchargement par l'utilisateur, le PDF sera régénéré avec
                la dernière version du template (sommaire premium + mini-QR scan-to-jump).
              </p>
            </div>

            {lastResult && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2" data-testid="pdf-cache-last-result">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                <div className="text-xs text-emerald-800">
                  <span className="font-semibold">Dernière purge :</span>{' '}
                  R2 supprimés <span className="font-mono">{lastResult.r2_deleted}</span> ·
                  disque <span className="font-mono">{lastResult.disk_deleted}</span>
                  {lastResult.errors?.length > 0 && (
                    <span className="text-orange-700"> · {lastResult.errors.length} erreur(s)</span>
                  )}
                </div>
              </div>
            )}

            <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeFinalized}
                onChange={(e) => setIncludeFinalized(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                data-testid="pdf-cache-include-finalized"
              />
              <span className="text-slate-700">
                Inclure aussi les <span className="font-semibold text-orange-700">souscriptions finalisées</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  ⚠️ Les PDFs finalisés (signés/datés) seront régénérés au prochain accès — peut altérer
                  l'horodatage si la régénération diffère du PDF d'origine.
                </span>
              </span>
            </label>

            <div className="flex justify-end gap-2">
              {!confirmOpen ? (
                <Button
                  variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={purging}
                  data-testid="pdf-cache-purge-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Purger le cache
                </Button>
              ) : (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-xs text-amber-800">
                    Confirmer la purge {includeFinalized ? '(drafts + finalisés)' : '(drafts uniquement)'} ?
                  </span>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setConfirmOpen(false)}
                    disabled={purging}
                    data-testid="pdf-cache-cancel-btn"
                  >
                    Annuler
                  </Button>
                  <Button
                    size="sm"
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={handlePurge}
                    disabled={purging}
                    data-testid="pdf-cache-confirm-btn"
                  >
                    {purging ? (
                      <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />Purge…</>
                    ) : (
                      'Confirmer la purge'
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
